import { useDispatch, useSelector } from "react-redux";
import { ActionType, AppDispatch, RootState } from "../reducer";
import { useEffect } from "react";
import { useAppId } from "../hook";
import { useFetchNotebookPage, useSelectedNotebookPageId } from "./page";
import { Message, MessageRole } from "../types/chat";
import {
  replyConversation as replyConversationApi,
  createConversation as createConversationApi,
} from "../api";

export const useChatId: () => [
  string | undefined,
  (chatId?: string) => void,
] = () => {
  const chatId = useSelector((state: RootState) => state.ui.chatId);
  const dispatch = useDispatch<AppDispatch>();
  const setChatId = (chatId?: string) =>
    dispatch({ type: ActionType.SET_CHAT_ID, chatId });
  return [chatId, setChatId];
};

export const useChatDrawerOpen: () => [
  boolean,
  (chatDrawerOpen: boolean) => void,
] = () => {
  const chatDrawerOpen = useSelector(
    (state: RootState) => state.ui.chatDrawerOpen,
  );
  const dispatch = useDispatch<AppDispatch>();
  const setChatDrawerOpen = (chatDrawerOpen: boolean) =>
    dispatch({ type: ActionType.SET_CHAT_DRAWER_OPEN, chatDrawerOpen });
  return [chatDrawerOpen, setChatDrawerOpen];
};

export const useToggleChatDrawerOpen: () => () => void = () => {
  const chatDrawerOpen = useSelector(
    (state: RootState) => state.ui.chatDrawerOpen,
  );
  const dispatch = useDispatch<AppDispatch>();
  const toggleChatDrawerOpen = () =>
    dispatch({
      type: ActionType.SET_CHAT_DRAWER_OPEN,
      chatDrawerOpen: !chatDrawerOpen,
    });
  return toggleChatDrawerOpen;
};

export const useCreateConversation: () => () => void = () => {
  const [chatId, setChatId] = useChatId();
  const [appId, setAppId] = useAppId();
  const [messages, setMessages] = useMessages();
  const [chatLoading, setChatLoading] = useChatLoading();

  const createConversation = () => {
    if (!appId) return;

    setChatId(undefined);
    setMessages([]);
    setChatLoading(true);

    return createConversationApi({
      appId,
    }).then((createConversationResponse) => {
      const chatId = createConversationResponse["chat-id"];
      setChatId(chatId);
      setChatLoading(false);
    });
  };

  return createConversation;
};

export const useReplyConversation: () => (config: {
  content: string;
  chatId?: string;
}) => Promise<any> = () => {
  const [chatIdFromState, setChatId] = useChatId();
  const [appId, setAppId] = useAppId();
  const addMessage = useAddMessage();
  const [chatLoading, setChatLoading] = useChatLoading();
  const [pageId] = useSelectedNotebookPageId();
  const fetchNotebookPage = useFetchNotebookPage(pageId);

  const replyConversation: (config: {
    content: string;
    chatId?: string;
  }) => Promise<any> = ({ content, chatId: chatIdFromConfig }) => {
    if (!appId) {
      throw new Error("no workspace found");
    }
    if (!pageId) {
      throw new Error("no page ID!");
    }

    const chatId = chatIdFromConfig ?? chatIdFromState;
    addMessage({ content, role: MessageRole.USER });
    setChatLoading(true);
    return replyConversationApi({ content, chatId: chatId!, pageId })
      .then((replyResponse) => {
        const reply = replyResponse.reply;
        addMessage({ content: reply, role: MessageRole.ASSISTANT });
        setChatLoading(false);
        return pageId ? fetchNotebookPage() : undefined;
      })
      .finally(() => {
        setChatLoading(false);
      });
  };

  return replyConversation;
};

export const useMessages: () => [
  Message[],
  (messages: Message[]) => void,
] = () => {
  const messages = useSelector((state: RootState) => state.ui.messages);
  const [appId, setAppId] = useAppId();
  const dispatch = useDispatch<AppDispatch>();
  const setMessages = (messages: Message[]) =>
    dispatch({ type: ActionType.SET_MESSAGES, messages });

  useEffect(() => {
    setMessages([]);
  }, [appId]);

  return [messages, setMessages];
};

export const useAddMessage: () => (message: Message) => void = () => {
  const dispatch = useDispatch<AppDispatch>();
  const addMessage = (message: Message) =>
    dispatch({ type: ActionType.ADD_MESSAGE, message });
  return addMessage;
};

export const useChatLoading: () => [
  boolean,
  (chatLoading: boolean) => void,
] = () => {
  const chatLoading = useSelector((state: RootState) => state.ui.chatLoading);
  const dispatch = useDispatch();
  const setChatLoading = (chatLoading: boolean) => {
    dispatch({ type: ActionType.SET_CHAT_LOADING, chatLoading });
  };
  return [chatLoading, setChatLoading];
};
